import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TokenStorageService } from '@/core/storage/token-storage.service';
import { AuthApiService } from '@/core/user/auth-api.service';
import { AuthStore } from '@/features/auth-store.service';
import { isTokenExpired } from '@/core/user/jwt-user';
import { APP_URLS } from '@/routes/app.paths';
import { buildWebSocketUrl } from './notification.config';
import { PushNotificationDispatcher } from '@/core/notification/push-notification-dispatcher.service';

const GOING_AWAY_CLOSE_CODE = 1001;
const IMMEDIATE_RETRY_DELAY_MS = 1000;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const STABLE_CONNECTION_MS = 10000;
const MAX_RECONNECT_ATTEMPTS = 10;

export const closureKind = ['EXPECTED', 'UNEXPECTED'] as const;

type ClosureKind = (typeof closureKind)[number];

@Injectable({ providedIn: 'root' })
export class WebSocketClient {
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly authApi = inject(AuthApiService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly pushNotificationDispatcher = inject(PushNotificationDispatcher);

  private socket: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private stabilityTimeout: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = INITIAL_BACKOFF_MS;
  private reconnectAttempts = 0;
  private stopped = true;

  connect(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.backoffMs = INITIAL_BACKOFF_MS;
    this.reconnectAttempts = 0;
    this.open();
  }

  disconnect(): void {
    this.stopped = true;
    this.cancelScheduledReconnect();
    this.cancelStabilityWindow();
    this.socket?.close();
    this.socket = null;
  }

  private open(): void {
    const accessToken = this.tokenStorage.getAccessToken();
    if (accessToken === null || accessToken === '') {
      void this.refreshTokenThenReconnect();
      return;
    }

    const socket = new WebSocket(buildWebSocketUrl(accessToken));
    this.socket = socket;

    socket.onopen = (): void => {
      if (this.socket !== socket) return;
      this.startStabilityWindow();
    };

    socket.onmessage = (event: MessageEvent): void => {
      if (this.socket !== socket) return;
      this.pushNotificationDispatcher.dispatch(event.data);
    };

    socket.onerror = (event: Event): void => {
      if (this.socket !== socket) return;
      console.error('WebSocket transport error', event);
    };

    socket.onclose = (event: CloseEvent): void => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.cancelStabilityWindow();
      void this.handleClosure(event.code === GOING_AWAY_CLOSE_CODE ? 'EXPECTED' : 'UNEXPECTED');
    };
  }

  private startStabilityWindow(): void {
    this.cancelStabilityWindow();
    this.stabilityTimeout = setTimeout(() => {
      this.backoffMs = INITIAL_BACKOFF_MS;
      this.reconnectAttempts = 0;
    }, STABLE_CONNECTION_MS);
  }

  private cancelStabilityWindow(): void {
    if (this.stabilityTimeout === null) return;
    clearTimeout(this.stabilityTimeout);
    this.stabilityTimeout = null;
  }

  private async handleClosure(closureKind: ClosureKind): Promise<void> {
    if (this.stopped) return;

    if (this.accessTokenIsValid()) {
      this.scheduleReconnect(closureKind);
      return;
    }

    await this.refreshTokenThenReconnect();
  }

  private async refreshTokenThenReconnect(): Promise<void> {
    if (this.isStopped()) return;

    const refreshed = await this.refreshAccessToken();
    if (this.isStopped()) return;

    if (!refreshed) {
      this.forceLogout();
      return;
    }

    this.backoffMs = INITIAL_BACKOFF_MS;
    this.reconnectAttempts = 0;
    this.scheduleReconnect('EXPECTED');
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    if (refreshToken === null || refreshToken === '') return false;

    try {
      this.authStore.storeAuthResult(await this.authApi.refresh(refreshToken));
      return true;
    } catch {
      return false;
    }
  }

  private isStopped(): boolean {
    return this.stopped;
  }

  private accessTokenIsValid(): boolean {
    const accessToken = this.tokenStorage.getAccessToken();
    if (accessToken === null || accessToken === '') return false;
    return !isTokenExpired(accessToken);
  }

  private forceLogout(): void {
    this.disconnect();
    this.authStore.logout();
    void this.router.navigate([APP_URLS.login]);
  }

  private scheduleReconnect(closureKind: ClosureKind): void {
    if (this.stopped) return;
    this.cancelScheduledReconnect();
    if (closureKind === 'UNEXPECTED' && this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.disconnect();
      return;
    }
    const delay = closureKind === 'EXPECTED' ? IMMEDIATE_RETRY_DELAY_MS : this.consumeExponentialBackoffDelay();
    this.reconnectTimeout = setTimeout(() => {
      this.open();
    }, delay);
  }

  private consumeExponentialBackoffDelay(): number {
    this.reconnectAttempts += 1;
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
    return delay;
  }

  private cancelScheduledReconnect(): void {
    if (this.reconnectTimeout === null) return;
    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = null;
  }
}
